import type { SnippetRegion } from 'flow/service/snippets/types';

export const FILTER_NAME_TO_API_FIELD: Record<string, string> = {
  'Integration Name': 'integrations',
  'Workflow Type': 'workflowType',
  'Date Created': 'dateCreated',
  'Date Updated': 'dateUpdated',
  'Snippet Name': 'name',
  Description: 'description',
  Region: 'regions',
  Archived: 'archived',
};

export const DATE_RANGE_FILTERS = new Set(['Date Created', 'Date Updated']);
export const TEXT_SEARCH_FILTERS = new Set(['Snippet Name', 'Description']);

export const ARCHIVED_FILTER_OPTIONS = [
  { label: 'Not Archived', value: 'not_archived' },
  { label: 'Archived', value: 'archived' },
] as const;

export const SORT_OPTIONS = [
  {
    value: 'NAME_ASC',
    label: 'Alphabetical Asc. (A→Z)',
    field: 'name',
    direction: 'ASC',
  },
  {
    value: 'NAME_DESC',
    label: 'Alphabetical Desc. (Z→A)',
    field: 'name',
    direction: 'DESC',
  },
  {
    value: 'RECENTLY_UPDATED',
    label: 'Recently Updated',
    field: 'dateUpdated',
    direction: 'DESC',
  },
] as const;

export type SortOption = typeof SORT_OPTIONS[number]['value'];

export const DEFAULT_SORT_OPTION: SortOption = 'RECENTLY_UPDATED';

export const SNIPPETS_PER_PAGE = 18;

// Snippet scope options for filtering between global and local snippets
export const SNIPPET_SCOPE = {
  GLOBAL: 'global',
  LOCAL: 'local',
} as const;

export type SnippetScope = typeof SNIPPET_SCOPE[keyof typeof SNIPPET_SCOPE];

export const DEFAULT_SNIPPET_SCOPE: SnippetScope = SNIPPET_SCOPE.GLOBAL;

export const getScopeLabel = (scope: SnippetScope, customerName?: string): string => {
  switch (scope) {
    case SNIPPET_SCOPE.LOCAL:
      return customerName || 'Local';
    case SNIPPET_SCOPE.GLOBAL:
    default:
      return 'Alloy';
  }
};

// Helper to generate scope options with dynamic customer name for LOCAL label
export const getScopeOptions = (args: {
  customerName?: string;
  wfContainsLinkedSnippet: boolean;
}) => {
  const { customerName, wfContainsLinkedSnippet } = args;
  return [
    {
      value: SNIPPET_SCOPE.GLOBAL,
      label: getScopeLabel(SNIPPET_SCOPE.GLOBAL),
      disabled: wfContainsLinkedSnippet,
      tooltip: wfContainsLinkedSnippet
        ? 'The Alloy Library is currently disabled for workflows with Linked Snippets.'
        : undefined,
    },
    {
      value: SNIPPET_SCOPE.LOCAL,
      label: getScopeLabel(SNIPPET_SCOPE.LOCAL, customerName),
    },
  ];
};

// Helper to get sort option config by value
export const getSortConfig = (value: SortOption) => (
  SORT_OPTIONS.find(option => option.value === value)!
);

// Mapping of production cell display names to region link values
// null is used to indicate that the snippet is global
export const PROD_CELL_DISPLAY_NAME_TO_REGION_MAP: Record<string, SnippetRegion | null> = {
  'Production (US)': 'US',
  'Production (Australia)': null, // Australia not in regionLinks, treat it as global
  'Production (UK)': 'UnitedKingdom',
  'Production (US) 2': 'US',
  Ally: 'US',
  Stash: 'US',
  UBS: 'US',
  USBank: 'US',
  Suncoast: 'US',
  Bread: 'US',
};

// Mapping of inner regions to outer regions
export const INNER_REGION_MAP: Record<SnippetRegion, SnippetRegion[]> = {
  US: ['NorthAmerica'],
  CA: ['NorthAmerica'],
  Europe: [],
  LatinAmerica: [],
  NorthAmerica: [],
  UnitedKingdom: ['Europe'],
  Asia: [],
};
